import React, { useEffect, useState } from 'react';
import { useCompact, useTapShield } from './_layout';
import { playWord, playGachaPull, playGachaRoll, playGachaPop } from './_audio';

const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
  plum: '#5B2E5E', purple: '#A06CD5',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';

const ASSET_BASE = '/games/vacab_gacha';
const BALL_IMAGES = [
  `${ASSET_BASE}/ball_1.png`,
  `${ASSET_BASE}/ball_2.png`,
  `${ASSET_BASE}/ball_3.png`,
  `${ASSET_BASE}/ball_4.png`,
  `${ASSET_BASE}/ball_5.png`,
  `${ASSET_BASE}/ball_7.png`,
  `${ASSET_BASE}/ball_8.png`,
  `${ASSET_BASE}/ball_9.png`,
];

// Positions (in % of stage) for balls inside the dome.
// Dome glass interior (below the "MAGIC VOCAB GACHA" sign): X 38-62%, Y 33-57%.
// 9 balls, spread out like loose gumballs sitting in the dome.
const DOME_BALLS = [
  // Row 1 — upper middle widest (3 balls)
  { x: 40, y: 41, size: 6.5, rot: 15 },
  { x: 50, y: 41, size: 6.5, rot: -10 },
  { x: 60, y: 41, size: 6.5, rot: 22 },
  // Row 2 — lower (2 balls, hex offset)
  { x: 45, y: 48, size: 6.5, rot: -18 },
  { x: 55, y: 48, size: 6.5, rot: 12 },
  // Row 3 — bottom (4 balls — the 2 outer ones were moved here from the top)
  { x: 41, y: 55, size: 6.5, rot: -8 },
  { x: 46, y: 54, size: 6.5, rot: 8 },
  { x: 54, y: 54, size: 6.5, rot: -5 },
  { x: 59, y: 55, size: 6.5, rot: 6 },
];

// Index of the ball that visually "falls out" — bottom-left, nearest to slot.
const DROPPER_INDEX = 5;

const ANIM_CSS = `
@keyframes vgMachineShake {
  0%,100% { transform: translate(0,0) rotate(0); }
  15% { transform: translate(-4px, 1px) rotate(-0.4deg); }
  30% { transform: translate(4px, -1px) rotate(0.4deg); }
  45% { transform: translate(-3px, 2px) rotate(-0.3deg); }
  60% { transform: translate(3px, -2px) rotate(0.3deg); }
  75% { transform: translate(-2px, 1px); }
  90% { transform: translate(2px, -1px); }
}
@keyframes vgBallIdle {
  0%,100% { transform: translate(-50%,-50%) rotate(var(--rot,0deg)) translate(0, 0); }
  25%     { transform: translate(-50%,-50%) rotate(calc(var(--rot,0deg) + 3deg)) translate(2px, -2px); }
  50%     { transform: translate(-50%,-50%) rotate(calc(var(--rot,0deg) - 2deg)) translate(0, -4px); }
  75%     { transform: translate(-50%,-50%) rotate(calc(var(--rot,0deg) + 1deg)) translate(-2px, -1px); }
}
@keyframes vgBallShake {
  0%,100% { transform: translate(-50%,-50%) rotate(var(--rot,0deg)); }
  20% { transform: translate(calc(-50% + var(--jx1,3px)), calc(-50% + var(--jy1,-2px))) rotate(calc(var(--rot,0deg) + 8deg)); }
  40% { transform: translate(calc(-50% + var(--jx2,-3px)), calc(-50% + var(--jy2,3px))) rotate(calc(var(--rot,0deg) - 10deg)); }
  60% { transform: translate(calc(-50% + var(--jx3,2px)), calc(-50% + var(--jy3,-3px))) rotate(calc(var(--rot,0deg) + 6deg)); }
  80% { transform: translate(calc(-50% + var(--jx4,-2px)), calc(-50% + var(--jy4,2px))) rotate(calc(var(--rot,0deg) - 6deg)); }
}
@keyframes vgBallDrop {
  0%   { transform: translate(-50%,-50%) rotate(0); opacity: 1; }
  35%  { transform: translate(-50%, 40%) rotate(160deg); opacity: 1; }
  60%  { transform: translate(-90%, 95%) rotate(280deg); opacity: 1; }
  100% { transform: translate(-130%, 170%) rotate(420deg); opacity: 0; }
}
@keyframes vgBallLand {
  0%   { transform: translate(-50%, -120%) scale(0.7); opacity: 0; }
  40%  { transform: translate(-50%, 8%)   scale(1.05); opacity: 1; }
  60%  { transform: translate(-50%, -4%)  scale(0.95); }
  80%  { transform: translate(-50%, 4%)   scale(1.02); }
  100% { transform: translate(-50%, 0)    scale(1); opacity: 1; }
}
@keyframes vgCrack {
  0%   { transform: translate(-50%,-50%) scale(0.4) rotate(-10deg); opacity: 0; }
  40%  { transform: translate(-50%,-50%) scale(1.15) rotate(4deg); opacity: 1; }
  100% { transform: translate(-50%,-50%) scale(1)   rotate(0); opacity: 1; }
}
@keyframes vgBurst {
  0%   { transform: translate(-50%,-50%) scale(0); opacity: 0; }
  30%  { transform: translate(-50%,-50%) scale(1.4); opacity: 1; }
  100% { transform: translate(-50%,-50%) scale(2.6); opacity: 0; }
}
@keyframes vgCardReveal {
  0%   { transform: translate(-50%, -50%) scale(0.5) rotate(-6deg); opacity: 0; }
  60%  { transform: translate(-50%, -50%) scale(1.06) rotate(2deg); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1)   rotate(0); opacity: 1; }
}
@keyframes vgConfetti {
  0%   { transform: translate(0,0) rotate(0); opacity: 1; }
  100% { transform: translate(var(--dx), var(--dy)) rotate(var(--dr)); opacity: 0; }
}
@keyframes vgFireworkSpark {
  0%   { transform: translate(0,0) scale(1);   opacity: 1; }
  60%  { transform: translate(calc(var(--dx) * 0.85), calc(var(--dy) * 0.85)) scale(1.1); opacity: 1; }
  100% { transform: translate(var(--dx), calc(var(--dy) + 30px)) scale(0.3); opacity: 0; }
}
@keyframes vgFireworkRing {
  0%   { transform: translate(-50%,-50%) scale(0);   opacity: 0.9; border-width: 6px; }
  60%  { transform: translate(-50%,-50%) scale(1.6); opacity: 0.5; border-width: 2px; }
  100% { transform: translate(-50%,-50%) scale(2.4); opacity: 0;   border-width: 1px; }
}
@keyframes vgFireworkFlash {
  0%   { transform: translate(-50%,-50%) scale(0.4); opacity: 0;   }
  20%  { transform: translate(-50%,-50%) scale(1.4); opacity: 0.95;}
  100% { transform: translate(-50%,-50%) scale(2.6); opacity: 0;   }
}
@keyframes vgFloat {
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
@keyframes vgHolo {
  0%,100% { background-position: 0% 50%; }
  50%     { background-position: 100% 50%; }
}
@keyframes vgLeverPull {
  0%,100% { transform: rotate(0); }
  35%     { transform: rotate(35deg); }
  70%     { transform: rotate(15deg); }
}
@keyframes vgPullBtnPulse {
  0%,100% { transform: translate(-50%,-50%) scale(1); box-shadow: 0 6px 0 #B8431E, 0 0 0 0 rgba(255,140,66,0.5); }
  50%     { transform: translate(-50%,-50%) scale(1.04); box-shadow: 0 6px 0 #B8431E, 0 0 0 14px rgba(255,140,66,0); }
}

.vg-holo {
  background: linear-gradient(120deg, #fef08a 0%, #f0abfc 25%, #93c5fd 50%, #6ee7b7 75%, #fef08a 100%);
  background-size: 200% 200%;
  animation: vgHolo 4s ease infinite;
}

.vg-confetti-piece {
  position: absolute;
  width: 10px; height: 14px;
  pointer-events: none;
  animation: vgConfetti 1.2s ease-out forwards;
}
`;

function injectCss() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('vocab-gacha-anim-css')) return;
  const el = document.createElement('style');
  el.id = 'vocab-gacha-anim-css';
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
}

// Firework burst — radial sparks + expanding ring + bright flash.
// Anchored to a parent with `position: absolute` at the target (x%, y%).
const Firework = ({ trigger, colors }) => {
  if (!trigger) return null;
  const SPARK_COUNT = 22;
  const PALETTE = colors || ['#FFD166', '#FF8C42', '#F4A6B8', '#A06CD5', '#5AC0B5', '#88C057', '#FFFFFF'];
  const sparks = Array.from({ length: SPARK_COUNT });
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
      {/* Bright central flash */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        width: 80, height: 80, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,209,102,0.6) 35%, transparent 70%)',
        animation: 'vgFireworkFlash 0.55s ease-out forwards',
      }} />
      {/* Expanding ring */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        width: 60, height: 60, borderRadius: '50%',
        border: '6px solid #FFD166',
        animation: 'vgFireworkRing 0.7s ease-out forwards',
      }} />
      {/* Second ring (delayed, different color) */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        width: 60, height: 60, borderRadius: '50%',
        border: '5px solid #F4A6B8',
        animation: 'vgFireworkRing 0.9s ease-out 0.15s forwards',
        opacity: 0,
      }} />
      {/* Radial sparks */}
      {sparks.map((_, i) => {
        const angle = (i / SPARK_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.2;
        const dist = 70 + Math.random() * 70;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const color = PALETTE[i % PALETTE.length];
        const isStreak = i % 3 === 0;
        return (
          <div
            key={`${trigger}-spark-${i}`}
            style={{
              position: 'absolute',
              left: '50%', top: '50%',
              width: isStreak ? 4 : 8,
              height: isStreak ? 14 : 8,
              marginLeft: isStreak ? -2 : -4,
              marginTop: isStreak ? -7 : -4,
              borderRadius: isStreak ? 2 : '50%',
              background: color,
              boxShadow: `0 0 8px ${color}, 0 0 14px ${color}`,
              transform: `rotate(${(angle * 180) / Math.PI + 90}deg)`,
              animationName: 'vgFireworkSpark',
              animationDuration: `${0.85 + Math.random() * 0.35}s`,
              animationTimingFunction: 'cubic-bezier(.22,.61,.36,1)',
              animationFillMode: 'forwards',
              '--dx': `${dx}px`,
              '--dy': `${dy}px`,
            }}
          />
        );
      })}
    </div>
  );
};

const Confetti = ({ trigger }) => {
  if (!trigger) return null;
  const pieces = Array.from({ length: 28 });
  const COLORS = [HT.yellow, HT.coral, HT.green, HT.teal, HT.pink, HT.purple];
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {pieces.map((_, i) => {
        const angle = (i / pieces.length) * Math.PI * 2;
        const dist = 140 + Math.random() * 100;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const dr = (Math.random() - 0.5) * 720;
        return (
          <div
            key={`${trigger}-${i}`}
            className="vg-confetti-piece"
            style={{
              left: '50%', top: '50%',
              background: COLORS[i % COLORS.length],
              borderRadius: i % 2 ? 2 : '50%',
              '--dx': `${dx}px`, '--dy': `${dy}px`, '--dr': `${dr}deg`,
            }}
          />
        );
      })}
    </div>
  );
};

// Set when the child taps "Tiếp tục" — the NEXT gacha (a fresh mount in the
// session flow) consumes it and pulls automatically, so back-to-back new words
// don't each demand a PULL tap. The very first gacha still gets the lever fun.
let autoPullNext = false;

export default function GachaGame({ payload, onAnswer, disabled }) {
  const compact = useCompact();
  const shield = useTapShield(700);
  // Phases:
  //   idle      → machine + dome of balls, PULL active
  //   shaking   → machine + balls jitter
  //   dropping  → one ball falls through the slot, others keep jiggling
  //   landed    → ball_ready.png shown at slot exit
  //   opening   → ball_up.png shown with burst
  //   revealed  → vocab card on top of dimmed scene
  const [phase, setPhase] = useState('idle');
  const [showBack, setShowBack] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [burstKey, setBurstKey] = useState(0);
  const [fireworkKey, setFireworkKey] = useState(0);
  // Pick a random ball image for the "lucky one" each pull.
  const [chosenBall, setChosenBall] = useState(BALL_IMAGES[0]);

  useEffect(() => { injectCss(); }, []);

  useEffect(() => {
    if (phase !== 'revealed') return;
    let cleanup = null;
    const t = setTimeout(() => {
      cleanup = playWord(payload.audio_url, payload.tts_text || payload.word_en);
    }, 350);
    return () => {
      clearTimeout(t);
      if (cleanup) cleanup();
    };
  }, [phase, payload.audio_url, payload.tts_text, payload.word_en]);

  // Auto-flip to the meaning side if the child hasn't tapped the card after 5s
  // (many kids just stared at the word without discovering the flip).
  useEffect(() => {
    if (phase !== 'revealed' || showBack) return;
    const t = setTimeout(() => setShowBack(true), 5000);
    return () => clearTimeout(t);
  }, [phase, showBack]);

  const pullLever = () => {
    if (phase !== 'idle' || disabled) return;
    playGachaPull();
    setChosenBall(BALL_IMAGES[Math.floor(Math.random() * BALL_IMAGES.length)]);
    setPhase('shaking');
    setTimeout(() => {
      setPhase('dropping');
      playGachaRoll();
    }, 700);
    setTimeout(() => setPhase('landed'), 1450);
    setTimeout(() => {
      setPhase('opening');
      setBurstKey(k => k + 1);
      setFireworkKey(k => k + 1);
      playGachaPop();
    }, 1900);
    setTimeout(() => { setPhase('revealed'); shield.rearm(); }, 2400);
  };

  // Consume the auto-pull handoff from the previous gacha's "Tiếp tục".
  useEffect(() => {
    if (!autoPullNext) return;
    autoPullNext = false;
    const t = setTimeout(() => pullLever(), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const replay = () => playWord(payload.audio_url, payload.tts_text || payload.word_en);

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    if (!shield.ok()) return; // ghost tap from the previous question
    if (dismissed) return;
    setDismissed(true);
    autoPullNext = true;
    if (onAnswer) onAnswer({ is_correct: true, user_answer: 'explored' });
  };

  const machineShake = (phase === 'shaking' || phase === 'dropping')
    ? 'vgMachineShake 0.32s linear infinite'
    : 'none';

  const bgImage = phase === 'idle'
    ? `${ASSET_BASE}/background_1.png`
    : `${ASSET_BASE}/background_2.png`;

  return (
    <div style={{
      width: 'calc(100vw - 32px)',
      maxWidth: 1600,
      marginLeft: 'calc(-50vw + 50% + 16px)',
      marginRight: 'calc(-50vw + 50% + 16px)',
      fontFamily: FONT_BODY,
      padding: 0,
      height: 'calc(100vh - 120px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Stage area — uses the painted background image */}
      <div style={{
        position: 'relative',
        width: compact ? '100%' : 'min(100%, calc((100vh - 120px) * 1672 / 941))',
        aspectRatio: compact ? '4 / 5' : '1672 / 941',
        borderRadius: 24,
        overflow: 'hidden',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 0.2s ease-out',
        boxShadow: '0 10px 30px rgba(139,111,71,0.18)',
      }}>
        {/* Top-right action buttons — shown when card is revealed */}
        {phase === 'revealed' && !dismissed && (
          <div style={{
            position: 'absolute', top: '3.5%', right: '2.5%',
            display: 'flex', gap: 10,
            zIndex: 10,
          }}>
            <button
              onClick={replay}
              aria-label="Phát lại âm thanh"
              title="Phát lại"
              style={stageBtn(HT.yellow, HT.yellowShadow)}
            >🔊</button>
            <button
              onClick={handleClose}
              aria-label="Đóng"
              title="Đóng"
              style={stageBtn(HT.white, HT.yellowShadow)}
            >✕</button>
          </div>
        )}
        {/* Stars counter overlay (matches the reference top-left chip) */}
        <div style={{
          position: 'absolute', top: '3.5%', left: '2.5%',
          padding: '0.6em 1.1em',
          background: 'rgba(255,255,255,0.92)',
          borderRadius: 16,
          border: `2px solid ${HT.yellow}`,
          boxShadow: '0 3px 0 rgba(139,111,71,0.15)',
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: FONT_DISPLAY,
          fontWeight: 800, color: HT.brownDark,
          fontSize: 'clamp(12px, 1.4vw, 18px)',
        }}>
          <span style={{ fontSize: '1.4em' }}>⭐</span>
          <span>{payload.stars_total ?? 0} STARS</span>
        </div>

        {/* Machine + dome content — shakes during pull */}
        <div style={{
          position: 'absolute', inset: 0,
          animation: machineShake,
          transformOrigin: '50% 70%',
          pointerEvents: 'none',
        }}>
          {/* Dome of capsules */}
          {DOME_BALLS.map((b, i) => {
            const img = BALL_IMAGES[i % BALL_IMAGES.length];
            const jitter = {
              '--rot': `${b.rot}deg`,
              '--jx1': `${(i % 2 ? 1 : -1) * (2 + (i % 3))}px`,
              '--jy1': `${-2 - (i % 2)}px`,
              '--jx2': `${(i % 2 ? -1 : 1) * (3 + (i % 2))}px`,
              '--jy2': `${2 + (i % 3)}px`,
              '--jx3': `${(i % 3 ? 1 : -1) * (2 + (i % 2))}px`,
              '--jy3': `${-3 - (i % 2)}px`,
              '--jx4': `${(i % 3 ? -1 : 1) * (2 + (i % 3))}px`,
              '--jy4': `${2 + (i % 2)}px`,
            };
            const isDropping = phase === 'dropping' && i === DROPPER_INDEX;
            const isHidden = (phase === 'dropping' || phase === 'landed' || phase === 'opening' || phase === 'revealed') && i === DROPPER_INDEX;

            // Idle: gentle independent bob (different duration & delay per ball).
            const idleDur = 2.6 + (i * 0.23) % 1.4;
            const idleDelay = (i * 0.31) % 1.5;
            let ballAnim = `vgBallIdle ${idleDur.toFixed(2)}s ease-in-out ${idleDelay.toFixed(2)}s infinite`;
            if (phase === 'shaking') ballAnim = `vgBallShake 0.25s linear infinite`;
            if (phase === 'dropping' && i !== DROPPER_INDEX) ballAnim = `vgBallShake 0.2s linear infinite`;
            if (isDropping) ballAnim = `vgBallDrop 0.75s cubic-bezier(.4,.05,.6,1) forwards`;

            return (
              <img
                key={i}
                src={img}
                alt=""
                style={{
                  position: 'absolute',
                  left: `${b.x}%`,
                  top: `${b.y}%`,
                  width: `${b.size}%`,
                  transform: `translate(-50%, -50%) rotate(${b.rot}deg)`,
                  animation: ballAnim,
                  filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.18))',
                  opacity: isHidden && !isDropping ? 0 : 1,
                  ...jitter,
                }}
                draggable={false}
              />
            );
          })}

          {/* Lever animation overlay (small red knob handle pull effect) */}
          {(phase === 'shaking' || phase === 'dropping') && (
            <div style={{
              position: 'absolute',
              left: '63.5%', top: '64%',
              width: '6%',
              transformOrigin: 'top center',
              animation: 'vgLeverPull 0.6s ease-in-out',
            }} />
          )}
        </div>

        {/* The "ready" capsule emerging from the slot */}
        {phase === 'landed' && (
          <img
            src={`${ASSET_BASE}/ball_ready.png`}
            alt=""
            style={{
              position: 'absolute',
              left: '37%',
              top: '78%',
              width: '12%',
              transform: 'translate(-50%, 0)',
              animation: 'vgBallLand 0.45s cubic-bezier(.3,1.4,.5,1) forwards',
              filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.25))',
              zIndex: 4,
            }}
            draggable={false}
          />
        )}

        {/* Cracked-open capsule with firework burst */}
        {phase === 'opening' && (
          <>
            {/* Firework anchor — sits at the capsule position */}
            <div style={{
              position: 'absolute',
              left: '37%', top: '72%',
              width: 0, height: 0,
              zIndex: 4,
            }}>
              <Firework trigger={fireworkKey} />
            </div>
            <img
              src={`${ASSET_BASE}/ball_up.png`}
              alt=""
              style={{
                position: 'absolute',
                left: '37%', top: '72%',
                width: '16%',
                transform: 'translate(-50%, -50%) scale(0.4)',
                animation: 'vgCrack 0.5s ease-out forwards',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                zIndex: 5,
              }}
              draggable={false}
            />
          </>
        )}

        {/* PULL button — overlays the yellow button area in the painted background */}
        {phase === 'idle' && !disabled && (
          <button
            onClick={pullLever}
            aria-label="Pull"
            style={{
              position: 'absolute',
              left: '50%', top: '78%',
              transform: 'translate(-50%, -50%)',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              animation: 'vgPullBtnPulse 1.6s ease-in-out infinite',
              borderRadius: 999,
              width: '14%',
              aspectRatio: '1 / 1',
              color: 'transparent',
              fontSize: 0,
            }}
          >PULL</button>
        )}

        {/* Status text strip at the bottom (in-game caption) */}
        <div style={{
          position: 'absolute',
          left: '50%', bottom: '4%',
          transform: 'translateX(-50%)',
          padding: '0.5em 1.4em',
          background: 'rgba(255,255,255,0.92)',
          borderRadius: 999,
          border: `2px dashed ${HT.coral}`,
          fontFamily: FONT_DISPLAY, fontWeight: 800,
          color: HT.brownDark,
          fontSize: 'clamp(12px, 1.4vw, 18px)',
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}>
          {phase === 'idle' && <>✨ Tap <span style={{ color: HT.coralDark }}>PULL</span> to get a new word! ✨</>}
          {phase === 'shaking' && <>The machine is shaking… 🎰</>}
          {phase === 'dropping' && <>A capsule is dropping! 🎉</>}
          {phase === 'landed' && <>Catching the capsule…</>}
          {phase === 'opening' && <>BOOM! 💥</>}
          {phase === 'revealed' && <>You got a new word! ⭐</>}
        </div>

        {/* Revealed card overlay — sits on top of dimmed scene */}
        {phase === 'revealed' && !dismissed && (
          <>
            {/* Dim overlay — tapping anywhere outside the card also continues */}
            <div
              onClick={handleClose}
              style={{
                position: 'absolute', inset: 0,
                background: 'rgba(45, 22, 60, 0.42)',
                backdropFilter: 'blur(2px)',
                cursor: 'pointer',
              }}
            />
            <Confetti trigger={burstKey} />

            {payload.is_rare && (
              <div style={{
                position: 'absolute', top: '8%', left: '50%',
                transform: 'translateX(-50%)',
                padding: '4px 14px', borderRadius: 999,
                fontSize: 12, fontWeight: 800, color: HT.brownDark,
                letterSpacing: 1.6, fontFamily: FONT_BODY, zIndex: 6,
              }} className="vg-holo">⭐ RARE WORD</div>
            )}

            <div
              onClick={() => setShowBack(v => !v)}
              style={{
                position: 'absolute',
                left: '50%', top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(78%, 460px)',
                maxHeight: '88%',
                borderRadius: 24,
                background: showBack ? HT.pinkBg : HT.white,
                border: `3px solid ${showBack ? HT.coral : HT.yellow}`,
                boxShadow: `0 10px 0 ${showBack ? HT.coralPress : HT.yellowShadow}, 0 14px 40px rgba(0,0,0,0.25)`,
                padding: '5% 5%',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: showBack ? 'flex-start' : 'center',
                cursor: 'pointer',
                animation: 'vgCardReveal 0.55s cubic-bezier(.2,1.3,.4,1) forwards',
                zIndex: 7,
                overflowY: 'auto',
              }}
            >
              {/* Close button on the flashcard */}
              <button
                onClick={handleClose}
                aria-label="Đóng"
                title="Đóng"
                style={{
                  position: 'absolute', top: 10, right: 12,
                  width: 34, height: 34, borderRadius: '50%',
                  border: 'none',
                  background: HT.white,
                  color: HT.brownDark,
                  fontFamily: FONT_DISPLAY,
                  fontSize: 16, fontWeight: 800, lineHeight: 1,
                  cursor: 'pointer',
                  boxShadow: `0 3px 0 ${HT.yellowShadow}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 9,
                }}
              >✕</button>

              {!showBack ? (
                <>
                  {payload.image_url ? (
                    <img
                      src={payload.image_url}
                      alt={payload.word_en}
                      style={{
                        maxHeight: '40%', maxWidth: '80%',
                        borderRadius: 14, marginBottom: 10,
                        filter: 'drop-shadow(0 4px 10px rgba(139,111,71,0.18))',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : null}
                  <div style={{
                    fontSize: 'clamp(28px, 4.2vw, 46px)', fontWeight: 700,
                    fontFamily: FONT_DISPLAY, color: HT.text, lineHeight: 1,
                    letterSpacing: -1, textAlign: 'center',
                  }}>{payload.word_en}</div>
                  {payload.pronunciation_uk ? (
                    <div style={{
                      marginTop: 6, fontSize: 'clamp(12px, 1.4vw, 16px)', color: HT.textSoft,
                      fontFamily: 'monospace',
                    }}>{payload.pronunciation_uk}</div>
                  ) : null}
                  {payload.pos ? (
                    <div style={{
                      marginTop: 4, fontSize: 'clamp(10px, 1.1vw, 13px)', fontStyle: 'italic',
                      color: HT.textMuted, fontWeight: 700,
                    }}>({payload.pos})</div>
                  ) : null}
                </>
              ) : (
                <>
                  <div style={{
                    fontSize: 'clamp(11px, 1.3vw, 14px)', fontWeight: 800, color: HT.coralDark,
                    fontFamily: FONT_BODY, letterSpacing: 2, marginBottom: 8, marginTop: 18,
                  }}>NGHĨA TIẾNG VIỆT</div>
                  <div style={{
                    fontSize: 'clamp(22px, 3.6vw, 36px)', fontWeight: 700,
                    fontFamily: FONT_DISPLAY, color: HT.text,
                    textAlign: 'center', lineHeight: 1.15,
                  }}>{payload.name_vi || '—'}</div>
                  <div style={{
                    marginTop: 12, padding: '6px 14px',
                    fontSize: 'clamp(14px, 1.8vw, 18px)', fontWeight: 700,
                    fontFamily: FONT_DISPLAY, color: HT.brownDark,
                    background: HT.yellowSoft, borderRadius: 999,
                  }}>{payload.word_en}</div>

                  {payload.definition_vi ? (
                    <div style={{
                      marginTop: 16, width: '100%', padding: '10px 14px',
                      background: HT.white, borderRadius: 14,
                      border: `2px dashed ${HT.greenDark}`,
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: HT.greenDark, letterSpacing: 1.5 }}>
                        📖 ĐỊNH NGHĨA
                      </div>
                      <div style={{ marginTop: 4, fontSize: 14, color: HT.text, fontWeight: 600, lineHeight: 1.5 }}>
                        {payload.definition_vi}
                      </div>
                    </div>
                  ) : null}

                  {payload.example_en ? (
                    <div style={{
                      marginTop: 10, width: '100%', padding: '10px 14px',
                      background: HT.white, borderRadius: 14,
                      border: `2px dashed ${HT.coral}`,
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: HT.coralDark, letterSpacing: 1.5 }}>
                        ✏️ VÍ DỤ
                      </div>
                      <div style={{
                        marginTop: 4, fontSize: 14, color: HT.text, fontStyle: 'italic',
                        fontWeight: 600, lineHeight: 1.5,
                      }}>
                        "{payload.example_en}"
                      </div>
                      {payload.example_vi ? (
                        <div style={{ marginTop: 4, fontSize: 13, color: HT.textSoft, fontWeight: 600 }}>
                          {payload.example_vi}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
              <div style={{
                marginTop: 12,
                fontSize: 11, fontWeight: 700, color: HT.textMuted,
                letterSpacing: 0.5,
              }}>🔄 Tap to flip</div>
            </div>

            {/* Big continue button — one obvious tap target for kids */}
            <button
              onClick={handleClose}
              style={{
                position: 'absolute',
                left: '50%', bottom: '3.5%',
                transform: 'translateX(-50%)',
                padding: '0.7em 2.2em',
                background: `linear-gradient(135deg, ${HT.green}, ${HT.greenDark})`,
                color: HT.white,
                border: `3px solid ${HT.white}`,
                borderRadius: 999,
                fontFamily: FONT_DISPLAY,
                fontSize: 'clamp(15px, 1.8vw, 20px)', fontWeight: 800,
                cursor: 'pointer',
                boxShadow: `0 4px 0 ${HT.greenDark}, 0 8px 20px rgba(0,0,0,0.25)`,
                zIndex: 8,
                whiteSpace: 'nowrap',
              }}
            >Tiếp tục →</button>
          </>
        )}
      </div>
    </div>
  );
}

function stageBtn(bg, shadowColor) {
  return {
    width: 'clamp(36px, 3.2vw, 48px)',
    height: 'clamp(36px, 3.2vw, 48px)',
    borderRadius: '50%',
    border: 'none',
    background: bg,
    color: HT.brownDark,
    fontFamily: FONT_DISPLAY,
    fontSize: 'clamp(14px, 1.5vw, 20px)',
    fontWeight: 800,
    lineHeight: 1,
    cursor: 'pointer',
    boxShadow: `0 3px 0 ${shadowColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}
